import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface Notification {
  id: string;
  user_id: string;
  type: 'new_review' | 'new_collaborator' | 'new_like';
  trip_id: string | null;
  actor_id: string | null;
  is_read: boolean;
  data: Record<string, any>;
  created_at: string;
  // Joined data
  trip_title?: string;
  trip_slug?: string;
  actor_name?: string;
}

export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
      return;
    }

    // Enrich with trip titles and actor names
    const enriched = await enrichNotifications(data || []);
    setNotifications(enriched);
    setUnreadCount(enriched.filter(n => !n.is_read).length);
    setLoading(false);
  }, [user]);

  const enrichNotifications = async (rawNotifications: any[]): Promise<Notification[]> => {
    if (rawNotifications.length === 0) return [];

    // Get unique trip IDs and actor IDs
    const tripIds = [...new Set(rawNotifications.map(n => n.trip_id).filter(Boolean))];
    const actorIds = [...new Set(rawNotifications.map(n => n.actor_id).filter(Boolean))];

    // Fetch trip titles
    let tripMap: Record<string, { title: string; slug: string }> = {};
    if (tripIds.length > 0) {
      const { data: trips } = await supabase
        .from('trips')
        .select('id, title, slug')
        .in('id', tripIds);
      trips?.forEach(t => {
        tripMap[t.id] = { title: t.title, slug: t.slug };
      });
    }

    // Fetch actor names
    let actorMap: Record<string, string> = {};
    if (actorIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', actorIds);
      profiles?.forEach(p => {
        actorMap[p.id] = p.full_name || '';
      });
    }

    return rawNotifications.map(n => ({
      ...n,
      trip_title: n.trip_id ? tripMap[n.trip_id]?.title : undefined,
      trip_slug: n.trip_id ? tripMap[n.trip_id]?.slug : undefined,
      actor_name: n.actor_id ? actorMap[n.actor_id] : undefined,
    }));
  };

  const markAsRead = useCallback(async (notificationId: string) => {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);

    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Realtime subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          const enriched = await enrichNotifications([payload.new]);
          if (enriched.length > 0) {
            setNotifications(prev => [enriched[0], ...prev].slice(0, 20));
            setUnreadCount(prev => prev + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
};
